import { zodResolver } from '@hookform/resolvers/zod';
import { AttachFile, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Divider } from '@mui/material';
import { endOfDay, format, isAfter, parseISO, subDays } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { GroupBase, OptionProps, components } from 'react-select';
import { useTheme } from 'styled-components/macro';

import { mapAgeToColor } from '../../helpers';
import AddAttachmentPopup from './components/AddAttachmentPopup/AddAttachmentPopup';
import ApproveTicketButton from './components/ApproveTicketButton/ApproveTicketButton';
import ChangeTicketClaimStatusPopup from './components/ChangeTicketClaimStatusPopup/ChangeTicketClaimStatusPopup';
import ChangeTicketPriorityPopup from './components/ChangeTicketPriorityPopup/ChangeTicketPriorityPopup';
import CloseReopenTicketButton from './components/CloseReopenTicketButton/CloseReopenTicketButton';
import DeleteTicketPopup from './components/DeleteTicketPopup/DeleteTicketPopup';
import { MessagesProvider } from './components/Messages/contexts/MessagesContext';
import Messages from './components/Messages/Messages';
import TicketApprovePopup from './components/TicketApprovePopup/TicketApprovePopup';
import TicketClosePopup from './components/TicketClosePopup/TicketClosePopup';
import TicketReopenPopup from './components/TicketReopenPopup/TicketReopenPopup';
import ViewAttachmentsPopup from './components/ViewAttachmentsPopup/ViewAttachmentsPopup';
import useChangeTicketDetailsForm, {
  ChangeTicketDetailsFormValuesT,
} from './hooks/useChangeTicketDetailsForm';
import useTicketDetailsPopup from './hooks/useTicketDetailsPopup';
import {
  ActionIconBtnStyled,
  CloseIconBtnStyled,
  ContentInfoStyled,
  ContentStyled,
  ControlsWrapStyled,
  CopyIconBtnStyled,
  FieldLabelStyled,
  FlexContainerScheduleStyled,
  FlexStyled,
  FormElementWrapStyled,
  FormStyled,
  HeaderControlsStyled,
  HeaderInfoStyled,
  HeaderStyled,
  MessagesWrapStyled,
  ScheduleHeaderLabelStyled,
  ScheduleInfoWrapStyled,
  TypographyStyled,
  getPopupContentStyle,
} from './TicketDetailsPopup.styled';
import validationSchema from './validationSchema';
import { getUsersLatestEulaAgreementStatus } from 'api/eula';
import CircleLoader from 'components/CircleLoader/CircleLoader';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import EulaAgreement from 'components/EulaAgreement/EulaAgreement';
import { FormDatePicker, FormInput, FormSelect } from 'components/Form';
import Popup from 'components/Popup/Popup';
import PriorityIcon from 'components/PriorityIcon/PriorityIcon';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { StorageE } from 'declarations/enums/storage';
import { StatusIdE, TicketAssigneeE } from 'declarations/enums/tickets';
import { SelectOptionT } from 'declarations/types/select';
import { getDecodedToken } from 'helpers/jwtToken';
import { formatInputAmount } from 'helpers/locale';
import { CloseIcon, SaveIcon } from 'icons';

const TicketDetailsPopup: FC = () => {
  /**
   * Hooks
   */
  const { isMobile, isDesktop } = useResponsive();
  const {
    ticketDetails,
    isLoadingTicketDetails,
    isTicketDetailsFetchingError,
    isTicketReopenPopupOpened,
    handleOpenTicketReopenPopup,
    handleCloseTicketReopenPopup,
    handleCloseTicketDetailsPopup,
    isTicketApprovePopupOpened,
    handleOpenTicketApprovePopup,
    handleCloseTicketApprovePopup,
    isTicketClosePopupOpened,
    handleOpenTicketClosePopup,
    handleCloseTicketClosePopup,
    isAddAttachmentPopupOpened,
    handleOpenAddAttachmentPopup,
    handleCloseAddAttachmentPopup,
    isViewAttachmentsPopupOpened,
    handleOpenViewAttachmentsPopup,
    handleCloseViewAttachmentsPopup,
    selectedTicketId,
    isChangeClaimStatusPopupOpened,
    handleCloseClaimStatusChangePopup,
    isChangePriorityPopupOpened,
    handleClosePriorityChangePopup,
    isDeleteTicketPopupOpened,
    handleCloseDeleteTicketPopup,
  } = useTicketDetailsPopup();

  const {
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions,
    isLoadingTicketSubcategoriesOptions,
    ticketSubcategoriesOptions,
    claimStatusesOptions,
    isLoadingClaimStatusesOptions,
    handleChangeTicketDetails,
    handleAmountBlur,
  } = useChangeTicketDetailsForm();

  const [assignToWarrCloud, setAssignToWarrCloud] = useState<boolean>(true);
  const { setTicketId } = useTickets();
  const theme = useTheme();

  const handleAssignToWarrCloudChange = (value: boolean) => {
    setAssignToWarrCloud(value);
  };

  const navigateToRelatedTicket = (direction: 'previous' | 'next') => {
    if (ticketDetails && selectedTicketId) {
      const currentIndex = ticketDetails?.relatedTicketIds.indexOf(selectedTicketId);
      let newIndex;

      if (direction === 'previous') {
        newIndex = Math.max(0, currentIndex - 1);
      } else {
        newIndex = Math.min(ticketDetails.relatedTicketIds.length - 1, currentIndex + 1);
      }

      const newTicketId = ticketDetails.relatedTicketIds[newIndex];
      setTicketId(newTicketId);
    }
  };

  const datePickerMaxDate = endOfDay(new Date());
  const [displayEulaAgreement, setDisplayEulaAgreement] = useState(false);
  const idToken = localStorage.getItem(StorageE.IdToken);
  const userId = getDecodedToken(idToken)?.['custom:user_id'] || null;

  useEffect(() => {
    async function checkEulaAgreement() {
      if (isTicketDetailsFetchingError && userId) {
        try {
          const statusResponse = await getUsersLatestEulaAgreementStatus(userId);
          setDisplayEulaAgreement(!statusResponse);
        } catch (error) {
          console.error('Error fetching EULA agreement:', error);
        }
      }
    }

    checkEulaAgreement();
  }, [isTicketDetailsFetchingError, userId]);

  const formMethods = useForm<ChangeTicketDetailsFormValuesT>({
    defaultValues: {
      ticketId: ticketDetails?.id,
      amount: ticketDetails?.amount.toString(),
      claimStatusId: ticketDetails?.claimStatusId,
      claimSubmittedDate: ticketDetails?.claimSubmittedDate
        ? new Date(ticketDetails?.claimSubmittedDate)
        : null,
      advisorNumber: ticketDetails?.advisorNumber,
      techNumber: ticketDetails?.techNumber,
      ticketPriorityId: ticketDetails?.ticketPriorityId,
      subCategoryId: ticketDetails?.subCategoryId,
      dmsScheduleAccountNumber: ticketDetails?.dmsScheduleAccountNumber || '',
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const { handleSubmit, setValue } = formMethods;

  useEffect(() => {
    if (ticketDetails) {
      setValue('ticketId', ticketDetails.id);
      setValue('amount', formatInputAmount(ticketDetails.amount) ?? '0.00');
      setValue('claimStatusId', ticketDetails.claimStatusId);
      setValue('advisorNumber', ticketDetails.advisorNumber);
      setValue('techNumber', ticketDetails.techNumber);
      setValue('ticketPriorityId', ticketDetails.ticketPriorityId);
      setValue('subCategoryId', ticketDetails.subCategoryId ? ticketDetails.subCategoryId : null);
      setValue('dmsScheduleAccountNumber', ticketDetails.dmsScheduleAccountNumber || '');
      const claimSubmittedDate = ticketDetails?.claimSubmittedDate
        ? utcToZonedTime((ticketDetails?.claimSubmittedDate + 'Z') as unknown as Date, 'UTC')
        : null;

      setValue('claimSubmittedDate', claimSubmittedDate);
    }
  }, [ticketDetails, setValue]);

  const { Option } = components;
  const OptionWithPriorityIcon = (
    props: JSX.IntrinsicAttributes & OptionProps<SelectOptionT, false, GroupBase<SelectOptionT>>
  ) => (
    <Option {...props}>
      <PriorityIcon priority={(props.data as SelectOptionT).label} />
      {(props.data as SelectOptionT).label}
    </Option>
  );

  const currentDate = Date.now();
  const ticketCreationDate = subDays(currentDate, ticketDetails?.age ?? 0);
  const checkIsDateAfter = (date: string): boolean => {
    return isAfter(new Date(date), new Date('1980-01-01'));
  };
  const isTicketClosed = ticketDetails?.statusId === StatusIdE.Closed;

  return (
    <Popup
      nested
      popupId={ScrollLockIdE.TicketDetailsPopup}
      contentStyle={getPopupContentStyle(isMobile, isDesktop)}
      isOpen={!!selectedTicketId}
      onClose={handleCloseTicketDetailsPopup}
      showCloseBtn={isLoadingTicketDetails || !!isTicketDetailsFetchingError}
      closeOnEscape={false}
    >
      {ticketDetails && !isLoadingTicketDetails && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" ml="-16px">
            <Box display="flex" alignItems="center">
              <CloseIconBtnStyled
                variant="transparent"
                size={isMobile ? 'md' : 'sm'}
                disabled={ticketDetails.relatedTicketIds.indexOf(selectedTicketId!) === 0}
                onClick={() => navigateToRelatedTicket('previous')}
                icon={<ChevronLeft />}
              />
              <Typography>
                {`${
                  ticketDetails.relatedTicketIds?.length
                    ? ticketDetails.relatedTicketIds.indexOf(selectedTicketId!) + 1
                    : 1
                } of ${ticketDetails.relatedTicketIds?.length || 1} Ticket${
                  ticketDetails.relatedTicketIds?.length > 1 ? 's' : ''
                }`}
              </Typography>

              <CloseIconBtnStyled
                variant="transparent"
                size={isMobile ? 'md' : 'sm'}
                disabled={
                  ticketDetails.relatedTicketIds.indexOf(selectedTicketId!) ===
                  ticketDetails.relatedTicketIds.length - 1
                }
                onClick={() => navigateToRelatedTicket('next')}
                icon={<ChevronRight />}
              />
            </Box>

            <CloseIconBtnStyled
              variant="transparent"
              size={isMobile ? 'md' : 'sm'}
              icon={<CloseIcon />}
              onClick={handleCloseTicketDetailsPopup}
            />
          </Box>

          <Divider sx={{ my: '8px' }} />

          <HeaderStyled>
            <HeaderInfoStyled>
              <Typography color="darkBlue" variant="h6">
                <FieldLabelStyled>Ticket#</FieldLabelStyled>
                {ticketDetails.number}
              </Typography>
              <Typography color="darkBlue" variant="h6">
                <FieldLabelStyled>RO#</FieldLabelStyled>
                {ticketDetails.roNumber}
              </Typography>
              <Typography color="darkBlue" variant="h6">
                <FieldLabelStyled>Line#</FieldLabelStyled>
                {ticketDetails.lineNumber}
              </Typography>
              {ticketDetails?.roDate && checkIsDateAfter(ticketDetails?.roDate) && (
                <Typography color="darkBlue" variant="h6">
                  <FieldLabelStyled>Opened</FieldLabelStyled>
                  {checkIsDateAfter(ticketDetails?.roDate)
                    ? format(parseISO(ticketDetails.roDate), 'MMM do, yyyy')
                    : null}
                </Typography>
              )}
              <Typography color="darkBlue" variant="h6">
                <FieldLabelStyled>Dealer</FieldLabelStyled>
                {ticketDetails.roofTop.name}
              </Typography>
              <Typography color={mapAgeToColor(ticketDetails.age)} variant="h6">
                <FieldLabelStyled>Age</FieldLabelStyled>
                {ticketDetails.age}
              </Typography>
              <Typography color="darkBlue" variant="h6">
                <FieldLabelStyled>Status</FieldLabelStyled>
                {ticketDetails.statusId}
              </Typography>
              <Typography color="darkBlue" variant="h6">
                <FieldLabelStyled>Closed in</FieldLabelStyled>
                {ticketDetails.closedInHours}
              </Typography>
              <Typography
                color={
                  ticketDetails.assignee.typeId === TicketAssigneeE.WarrCloud
                    ? 'purple'
                    : 'darkBlue'
                }
                variant="h6"
              >
                <FieldLabelStyled>Assigned to</FieldLabelStyled>
                {ticketDetails.assigneeName === 'OEM' ? 'OEM' : ticketDetails.assignee.typeId}
              </Typography>
              {!isMobile && (
                <Typography color="darkBlue" variant="h6">
                  <FieldLabelStyled>Category</FieldLabelStyled>
                  {ticketDetails.categoryName}
                </Typography>
              )}
              {ticketDetails.dmsScheduleAccountNumber && (
                <Typography color="darkBlue" variant="h6">
                  <FieldLabelStyled>Account</FieldLabelStyled>
                  {ticketDetails.dmsScheduleAccountNumber}
                </Typography>
              )}
            </HeaderInfoStyled>
            <HeaderControlsStyled>
              <ApproveTicketButton
                isApprovedByDealer={ticketDetails.isApprovedByDealer}
                isApprovalRequested={ticketDetails.isApprovalRequested}
                handleOpenTicketApprovePopup={handleOpenTicketApprovePopup}
              />
              <CloseReopenTicketButton
                ticketStatus={ticketDetails.statusId}
                isUserTicketCreator={ticketDetails.userIsCreator}
                handleOpenTicketReopenPopup={handleOpenTicketReopenPopup}
                handleOpenTicketClosePopup={handleOpenTicketClosePopup}
              />
              {/* Temp hidden WARR-4972 */}
              {/* <DeleteTicketButton
                isUserTicketCreator={ticketDetails.userIsCreator}
                handleOpenDeleteTicketPopup={handleOpenDeleteTicketPopup}
              /> */}

              <CopyIconBtnStyled
                variant="transparent"
                size={isMobile ? 'md' : 'sm'}
                icon={
                  <AttachFile
                    sx={{ color: theme.colors.primary.variant8, width: 18, height: 24 }}
                  />
                }
                onClick={handleOpenViewAttachmentsPopup}
                title="View all attachments"
              />
            </HeaderControlsStyled>
          </HeaderStyled>
          <ContentStyled>
            <ContentInfoStyled>
              <FormProvider {...formMethods}>
                <FormStyled role="form" onSubmit={handleSubmit(handleChangeTicketDetails)}>
                  {ticketDetails.categoryName.toLowerCase() === 'schedule' && (
                    <FlexContainerScheduleStyled>
                      <ScheduleHeaderLabelStyled>Schedule</ScheduleHeaderLabelStyled>
                      <ScheduleInfoWrapStyled>
                        {ticketDetails.roClosedDate !== null &&
                          ticketDetails.roClosedDate !== undefined && (
                            <TypographyStyled color="darkBlue" variant="h6">
                              <FieldLabelStyled>Closed RO date</FieldLabelStyled>
                              {checkIsDateAfter(ticketDetails?.roClosedDate)
                                ? format(new Date(ticketDetails?.roClosedDate), 'MMM do, yyyy')
                                : null}
                            </TypographyStyled>
                          )}
                        {ticketDetails.closedROAge !== null &&
                          ticketDetails.closedROAge !== undefined && (
                            <TypographyStyled
                              color="darkBlue"
                              variant="h6"
                              style={{ textAlign: 'center' }}
                            >
                              <FieldLabelStyled>Closed RO age</FieldLabelStyled>
                              {ticketDetails.closedROAge}
                            </TypographyStyled>
                          )}
                        <TypographyStyled color="darkBlue" variant="h6">
                          <FieldLabelStyled>Last updated</FieldLabelStyled>
                          {checkIsDateAfter(ticketDetails?.lastUpdatedDateTime)
                            ? format(new Date(ticketDetails?.lastUpdatedDateTime), 'MMM do, yyyy')
                            : format(new Date(ticketCreationDate), 'MMM do, yyyy')}
                        </TypographyStyled>
                      </ScheduleInfoWrapStyled>
                      <FormElementWrapStyled>
                        <FormSelect
                          label="Subcategory"
                          name="subCategoryId"
                          placeholder="Subcategories"
                          options={ticketSubcategoriesOptions}
                          isLoading={isLoadingTicketSubcategoriesOptions}
                        />
                      </FormElementWrapStyled>
                    </FlexContainerScheduleStyled>
                  )}
                  <FormElementWrapStyled>
                    <FormInput
                      label="Amount $"
                      name="amount"
                      placeholder="Amount $"
                      onBlur={handleAmountBlur(setValue)}
                      disabled={!ticketDetails.userIsOwner}
                    />
                  </FormElementWrapStyled>
                  <FormElementWrapStyled>
                    <FormSelect
                      label="Choose claim status"
                      name="claimStatusId"
                      placeholder="Claim statuses"
                      options={claimStatusesOptions}
                      isLoading={isLoadingClaimStatusesOptions}
                    />
                  </FormElementWrapStyled>
                  <FormElementWrapStyled>
                    <FormDatePicker
                      label="Choose claim submitted date"
                      name="claimSubmittedDate"
                      placeholder="Claim submitted date"
                      maxDate={datePickerMaxDate}
                      disabled={!ticketDetails.userIsOwner}
                    />
                  </FormElementWrapStyled>
                  <FormElementWrapStyled>
                    <FormInput
                      label="Advisor Number"
                      name="advisorNumber"
                      placeholder="Advisor Number"
                    />
                  </FormElementWrapStyled>
                  <FormElementWrapStyled>
                    <FormInput label="Tech Number" name="techNumber" placeholder="Tech Number" />
                  </FormElementWrapStyled>
                  <FormElementWrapStyled>
                    <FormSelect
                      label="Choose ticket priority"
                      name="ticketPriorityId"
                      placeholder="Ticket priorities"
                      options={ticketPrioritiesOptions}
                      isLoading={isLoadingTicketPrioritiesOptions}
                      components={{ Option: OptionWithPriorityIcon }}
                    />
                  </FormElementWrapStyled>
                  <ControlsWrapStyled justify={isMobile ? 'center' : 'flex-start'}>
                    <ActionIconBtnStyled
                      icon={<SaveIcon />}
                      title="Save fields"
                    ></ActionIconBtnStyled>
                  </ControlsWrapStyled>
                </FormStyled>
              </FormProvider>
            </ContentInfoStyled>
            <MessagesWrapStyled
              style={{
                height: ticketDetails.categoryName.toLowerCase() === 'schedule' ? 650 : 485,
              }}
            >
              <Messages
                requiresAttachment={ticketDetails.requiresAttachment}
                roofTopName={ticketDetails.roofTop.name}
                handleOpenAddAttachmentPopup={handleOpenAddAttachmentPopup}
                onAssignToWarrCloudChanged={handleAssignToWarrCloudChange}
                isTicketClosed={isTicketClosed}
              />
            </MessagesWrapStyled>
          </ContentStyled>
        </>
      )}

      {isLoadingTicketDetails && (
        <FlexStyled align="center" justify="center">
          <CircleLoader />
        </FlexStyled>
      )}
      {isTicketDetailsFetchingError && (
        <FlexStyled direction="column" align="center" justify="center">
          <ErrorSection />
        </FlexStyled>
      )}
      {isTicketDetailsFetchingError && displayEulaAgreement && <EulaAgreement />}

      <TicketReopenPopup
        ticketId={ticketDetails?.id || ''}
        isOpen={isTicketReopenPopupOpened}
        onClose={handleCloseTicketReopenPopup}
      />
      <TicketApprovePopup
        ticketId={ticketDetails?.id || ''}
        isOpen={isTicketApprovePopupOpened}
        onClose={handleCloseTicketApprovePopup}
      />
      <TicketClosePopup
        ticketId={ticketDetails?.id || ''}
        isOpen={isTicketClosePopupOpened}
        onClose={handleCloseTicketClosePopup}
      />
      <AddAttachmentPopup
        ticketId={ticketDetails?.id || ''}
        categoryName={ticketDetails?.categoryName || ''}
        isOpen={isAddAttachmentPopupOpened}
        assignToWarrCloud={assignToWarrCloud}
        onClose={handleCloseAddAttachmentPopup}
      />
      <ViewAttachmentsPopup
        ticketId={ticketDetails?.id || ''}
        ticketStatus={ticketDetails?.statusId as StatusIdE}
        isOpen={isViewAttachmentsPopupOpened}
        onClose={handleCloseViewAttachmentsPopup}
      />
      <ChangeTicketClaimStatusPopup
        ticketId={ticketDetails?.id || ''}
        claimStatusId={ticketDetails?.claimStatusId || 0}
        isOpen={isChangeClaimStatusPopupOpened}
        onClose={handleCloseClaimStatusChangePopup}
      />
      <ChangeTicketPriorityPopup
        ticketId={ticketDetails?.id || ''}
        ticketPriorityId={ticketDetails?.ticketPriorityId || 0}
        isOpen={isChangePriorityPopupOpened}
        onClose={handleClosePriorityChangePopup}
      />
      <DeleteTicketPopup
        ticketId={ticketDetails?.id || ''}
        isOpen={isDeleteTicketPopupOpened}
        onClose={handleCloseDeleteTicketPopup}
      />
    </Popup>
  );
};

const TicketDetailsPopupWithMessagesProvider: FC = () => {
  const { selectedTicketId } = useTickets();

  return (
    <MessagesProvider ticketId={selectedTicketId || ''}>
      <TicketDetailsPopup />
    </MessagesProvider>
  );
};

export default TicketDetailsPopupWithMessagesProvider;
